import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Users,
  ListChecks,
  MessageSquareText,
  AlertOctagon,
  Lightbulb,
  Database,
  FlaskConical,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { parseGooseBerryMarkdown } from "@/lib/markdownParser";
import { SectionStack } from "@/components/gooseberry-output/SectionCard";
import { SyntheticUsersPanel } from "@/components/gooseberry-output/SyntheticUsersPanel";
import { SourcesAndConfidence } from "@/components/gooseberry-output/SourcesAndConfidence";
import { RawTerminal } from "@/components/gooseberry-output/RawTerminal";

const TAB_MAPPING = {
  summary: ["context", "goal", "design phase", "method"],
  feedback: ["feedback", "insights"],
  assumptions: ["assumptions", "limitations"],
  interview: ["interview questions"],
  next: ["next methods"],
  sources: ["databases used", "overall confidence"],
  evidence: ["evidence & assumption check"],
};

function getTabDefs(mode, language) {
  const nl = language === "nl";
  if (mode === "dialogue") {
    return [
      { id: "raw", label: nl ? "Ruwe output" : "Raw output", icon: FlaskConical },
    ];
  }
  return [
    { id: "summary", label: nl ? "Samenvatting" : "Summary", icon: ListChecks },
    { id: "users", label: nl ? "Synthetische gebruikers" : "Synthetic users", icon: Users },
    { id: "feedback", label: "Feedback", icon: MessageSquareText },
    { id: "assumptions", label: nl ? "Aannames" : "Assumptions", icon: AlertOctagon },
    { id: "interview", label: nl ? "Interviewvragen" : "Interview questions", icon: ListChecks },
    { id: "next", label: nl ? "Volgende stappen" : "Next steps", icon: Lightbulb },
    { id: "evidence", label: nl ? "Evidence-check" : "Evidence check", icon: ShieldCheck },
    { id: "sources", label: nl ? "Bronnen & vertrouwen" : "Sources & confidence", icon: Database },
    { id: "raw", label: nl ? "Ruwe output" : "Raw terminal", icon: FlaskConical },
  ];
}

export function GooseBerryOutput({
  markdown,
  loading,
  mode,
  projectName,
  sourcesUsed = [],
  fidelity,
  visionUsed = true,
  modelInfo = {},
  groundingInfo = {},
  onCopy,
  onDownloadMd,
  onDownloadPdf,
  feedbackContext,
  onRegenerateWithCorrections,
  regenerating,
  correctionCount = 0,
  language = "en",
}) {
const [tab, setTab] = useState(mode === "dialogue" ? "raw" : "summary");
const tabDefs = useMemo(() => getTabDefs(mode, language), [mode, language]);
const parsed = useMemo(() => parseGooseBerryMarkdown(markdown), [markdown]);

  useEffect(() => {
    if (mode === "dialogue" && tab !== "raw") {
      setTab("raw");
      return;
    }
    if (!tabDefs.some((item) => item.id === tab)) {
      setTab(mode === "dialogue" ? "raw" : "summary");
    }
  }, [mode, tab, tabDefs]);

  if (loading) {
    return <LoadingPanel mode={mode} language={language} />;
  }
  if (!markdown) {
    return <EmptyPanel language={language} />;
  }

  return (
    <section className="space-y-4" data-testid="synberry-output">
      <HeaderStrip
        projectName={projectName}
        mode={mode}
        fidelity={fidelity}
        overallConfidence={parsed.overallConfidence}
        onCopy={onCopy}
        onDownloadMd={onDownloadMd}
        onDownloadPdf={onDownloadPdf}
        language={language}
      />

      <TransparencyPanel
        modelInfo={modelInfo}
        groundingInfo={groundingInfo}
        visionUsed={visionUsed}
        language={language}
      />

      {!visionUsed && (
        <div
          className="rounded-2xl border border-amber-300 bg-amber-50 p-4 sm:p-5"
          data-testid="gooseberry-vision-skipped-banner"
        >
          <div className="text-[11px] uppercase tracking-wider text-amber-700">{language === "nl" ? "Let op" : "Heads up"}</div>
          <div className="mt-1 text-sm text-amber-900">
            {language === "nl" ? (
              <>
                De assistant kon je afbeelding in deze run niet analyseren. Het rapport hieronder is daarom alleen
                gebaseerd op je <strong>tekstuele context</strong> (project, doelgroep, aannames en research metadata).
                Visuele kritiek is overgeslagen. Probeer <strong> Run simulatie </strong> opnieuw met een duidelijkere afbeelding wanneer dat kan.
              </>
            ) : (
              <>
                The assistant could not analyze your image this run, so the report below is
                grounded in your <strong>textual context</strong> only (project, target group,
                assumptions, research metadata). Visual critique is skipped, try
                <strong> Run simulation </strong> again with a clearer image when you can.
              </>
            )}
          </div>
        </div>
      )}

      {correctionCount > 0 && onRegenerateWithCorrections && (
        <div
          className="rounded-2xl border-2 border-[#61CBA3] bg-[rgba(97,203,163,0.10)] p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
          data-testid="gooseberry-regenerate-cta"
        >
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-[#61CBA3]">
              {language === "nl" ? "Je correcties staan klaar" : "Your corrections are ready"}
            </div>
            <div className="mt-1 text-sm text-black">
              {language === "nl" ? (
                <>
                  Je hebt{" "}
                  <strong className="text-black">
                    {correctionCount} correctie{correctionCount === 1 ? "" : "s"}
                  </strong>{" "}
                  gemarkeerd. GooseBerry kan de analyse opnieuw draaien met je opmerkingen erbij.
                </>
              ) : (
                <>
                  You have flagged{" "}
                  <strong className="text-black">
                    {correctionCount} correction{correctionCount === 1 ? "" : "s"}
                  </strong>
                  . GooseBerry can rerun the analysis with your notes included.
                </>
              )}
            </div>
          </div>
          <Button
            type="button"
            onClick={onRegenerateWithCorrections}
            disabled={!!regenerating}
            className="rounded-xl bg-[#61CBA3] text-white hover:bg-[#4DAF8B] disabled:opacity-60"
            data-testid="gooseberry-regenerate-button"
          >
            {regenerating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            {language === "nl" ? "Opnieuw genereren met mijn correcties" : "Regenerate with my corrections"}
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} data-testid="synberry-output-tabs">
        <TabsList className="flex flex-wrap h-auto bg-white border border-black/15 rounded-2xl p-1 gap-1">
          {tabDefs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="rounded-xl px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#61CBA3] data-[state=active]:text-white"
                data-testid={`synberry-output-tab-${t.id}`}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <SectionStack sectionKeys={TAB_MAPPING.summary} parsed={parsed} testid="synberry-output-summary" feedbackContext={feedbackContext} language={language} />
        </TabsContent>
        <TabsContent value="users" className="mt-4">
          <SyntheticUsersPanel
            users={parsed.syntheticUsers}
            fallback={parsed.bySection["synthetic users"] || parsed.bySection["dialogue"]}
            feedbackContext={feedbackContext}
            language={language}
          />
        </TabsContent>
        <TabsContent value="feedback" className="mt-4">
          <SectionStack sectionKeys={TAB_MAPPING.feedback} parsed={parsed} testid="synberry-output-feedback" feedbackContext={feedbackContext} language={language} />
        </TabsContent>
        <TabsContent value="assumptions" className="mt-4">
          <SectionStack sectionKeys={TAB_MAPPING.assumptions} parsed={parsed} testid="synberry-output-assumptions" feedbackContext={feedbackContext} language={language} />
        </TabsContent>
        <TabsContent value="interview" className="mt-4">
          <div className="rounded-2xl border border-black/10 bg-[rgba(97,203,163,0.06)] p-4 mb-3 text-sm text-black/75">
            {language === "nl" ? (
              <>
                Deze vragen zijn een <strong className="text-black">startpunt voor vervolgonderzoek</strong> met
                echte gebruikers. Het zijn geen gevalideerde vragen, controleer en pas ze aan voordat je ze gebruikt.
              </>
            ) : (
              <>
                These questions are a <strong className="text-black">starting point for follow-up research</strong> with
                real users. They are not validated questions, review and adapt them before use.
              </>
            )}
          </div>
          <SectionStack sectionKeys={TAB_MAPPING.interview} parsed={parsed} testid="synberry-output-interview" feedbackContext={feedbackContext} language={language} />
        </TabsContent>
        <TabsContent value="next" className="mt-4">
          <SectionStack sectionKeys={TAB_MAPPING.next} parsed={parsed} testid="synberry-output-next" feedbackContext={feedbackContext} language={language} />
        </TabsContent>
        <TabsContent value="evidence" className="mt-4">
          <div className="rounded-2xl border border-black/10 bg-[rgba(97,203,163,0.06)] p-4 mb-3 text-sm text-black/75">
            {language === "nl" ? (
              <>
                Deze sectie deelt belangrijke claims uit de analyse in op{" "}
                <strong className="text-black">bewijskracht</strong>. Gebruik dit om speculatieve claims te herkennen
                voordat je het rapport deelt.
              </>
            ) : (
              <>
                This section classifies each important claim from the analysis by{" "}
                <strong className="text-black">evidence strength</strong>. Use it to spot
                speculative claims before sharing the report.
              </>
            )}
          </div>
          <SectionStack sectionKeys={TAB_MAPPING.evidence} parsed={parsed} testid="synberry-output-evidence" feedbackContext={feedbackContext} language={language} />
        </TabsContent>
        <TabsContent value="sources" className="mt-4">
          <SourcesAndConfidence
            parsed={parsed}
            sourcesUsed={sourcesUsed}
            feedbackContext={feedbackContext}
            modelInfo={modelInfo}
            groundingInfo={groundingInfo}
            language={language}
          />
        </TabsContent>
        <TabsContent value="raw" className="mt-4">
          <RawTerminal markdown={markdown} mode={mode} projectName={projectName} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

function LoadingPanel({ mode, language }) {
  const nl = language === "nl";
  const steps = nl
    ? ["Lokale codebooks en publieke metadata checken", "Je ontwerp + context versturen", "Rapport opbouwen"]
    : ["Checking local codebooks and public metadata", "Sending your artifact + context for analysis", "Building the structured report"];
  return (
    <section
      className="rounded-2xl border border-black/10 bg-white p-8 text-center"
      data-testid="synberry-output-loading"
    >
      <Loader2 className="h-7 w-7 mx-auto animate-spin text-[#61CBA3]" />
      <div className="mt-4 font-display text-xl text-black">
        {nl ? "Genereren" : "Generating"} {mode === "dialogue" ? (nl ? "synthetische-gebruikersdialoog" : "synthetic-user dialogue") : (nl ? "gestructureerde feedback" : "structured feedback")}...
      </div>
      <div className="mt-1 text-sm text-black/60">
        {nl
          ? "We zoeken research metadata, bouwen de analyse-input en vragen de assistant om een Markdown rapport. Dit duurt meestal 30-90 seconden."
          : "We are searching research metadata, building the analysis payload, and asking the assistant for a Markdown report. This usually takes 30-90 seconds."}
      </div>
      <ul className="mt-5 max-w-md mx-auto space-y-2 text-left text-sm text-black/70">
        {steps.map((s, i) => (
          <li key={s} className="rounded-xl border border-black/10 bg-white p-3 flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgba(97,203,163,0.18)] text-[#61CBA3] text-[10px] font-semibold">{i + 1}</span>
            {s}
          </li>
        ))}
      </ul>
    </section>
  );
}

function EmptyPanel({ language }) {
  const nl = language === "nl";
  return (
    <section
      className="rounded-2xl border border-dashed border-black/15 bg-white p-8 text-center"
      data-testid="synberry-output-empty"
    >
      <FlaskConical className="h-6 w-6 mx-auto text-[#61CBA3]" />
      <div className="mt-3 font-display text-lg text-black">{nl ? "Nog geen output" : "No output yet"}</div>
      <div className="mt-1 text-sm text-black/60">
        {nl ? "Upload een ontwerp, vul de context in en druk daarna op " : "Upload a design artifact, fill the context, then press "}
        <strong className="text-black">{nl ? "Run simulatie" : "Run simulation"}</strong>.
      </div>
    </section>
  );
}

function TransparencyPanel({ modelInfo = {}, groundingInfo = {}, visionUsed, language = "en" }) {
  const nl = language === "nl";
  const hasModels = !!(modelInfo.analysisModel || modelInfo.visionModel || modelInfo.evidenceModel);
  const hasGrounding = Object.keys(groundingInfo || {}).length > 0;
  if (!hasModels && !hasGrounding) return null;

  const modelLabels = [
    modelInfo.analysisModel ? `${nl ? "Analyse" : "Analysis"}: ${modelInfo.analysisModel}` : null,
    modelInfo.visionModel && visionUsed ? `Vision: ${modelInfo.visionModel}` : null,
    modelInfo.evidenceModel ? `${nl ? "Evidence-check" : "Evidence check"}: ${modelInfo.evidenceModel}` : null,
  ].filter(Boolean);

  const groundingLabels = [
    groundingInfo.usesLocalCodebooks
      ? `${nl ? "Lokale codebooks" : "Local codebooks"}: ${groundingInfo.localCodebookSnippetCount || 0} ${nl ? "fragmenten" : "excerpts"}`
      : (nl ? "Lokale codebooks: geen match" : "Local codebooks: no match"),
    `${nl ? "Publieke metadata" : "Public metadata"}: ${groundingInfo.researchMetadataCount || 0}`,
    `${nl ? "Geselecteerde open datasets" : "Selected open datasets"}: ${groundingInfo.selectedOpenDatasetCount || 0}`,
    nl ? "Raw paneldata: niet gebruikt" : "Raw panel data: not used",
  ];

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 sm:p-5" data-testid="gooseberry-transparency-panel">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 text-[#61CBA3]" />
        <div className="min-w-0">
          <div className="font-display text-base font-semibold text-black">{nl ? "Model- en researchtransparantie" : "Model and research transparency"}</div>
          {modelLabels.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {modelLabels.map((label) => (
                <Badge key={label} variant="outline" className="rounded-full border-[#61CBA3] text-[#61CBA3] bg-white">
                  {label}
                </Badge>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {groundingLabels.map((label) => (
              <Badge key={label} variant="outline" className="rounded-full border-black/15 text-black bg-white">
                {label}
              </Badge>
            ))}
          </div>
          <p className="mt-2 text-xs text-black/60">
            {nl
              ? "GooseBerry gebruikt lokale codebook-fragmenten en publieke/geselecteerde metadata alleen als contextuele onderbouwing. Het analyseert geen raw LISS Panel-data of datasets met beperkte toegang."
              : "GooseBerry uses local codebook excerpts and public/selected metadata as grounding only. It does not analyze raw LISS Panel data or permission-restricted datasets."}
          </p>
        </div>
      </div>
    </div>
  );
}

function HeaderStrip({ projectName, mode, fidelity, overallConfidence, onCopy, onDownloadMd, onDownloadPdf, language = "en" }) {
  const nl = language === "nl";
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[12px] uppercase tracking-[0.18em] text-[#61CBA3] font-medium">Output</p>
        <h2 className="font-display text-lg sm:text-xl font-semibold text-black truncate">
          {projectName ? projectName : nl ? "Naamloos project" : "Untitled project"}
        </h2>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
          <Badge variant="outline" className="rounded-full border-black/15 text-black bg-white">
            {nl ? "Modus" : "Mode"}: {mode === "dialogue" ? (nl ? "Dialoog" : "Dialogue") : "Feedback"}
          </Badge>
          {fidelity && (
            <Badge variant="outline" className="rounded-full border-black/15 text-black bg-white">
              {nl ? "Uitwerking" : "Fidelity"}: {fidelity}
            </Badge>
          )}
          {overallConfidence && (
            <Badge className="rounded-full border border-[#61CBA3] bg-[rgba(97,203,163,0.18)] text-black">
              {nl ? "Algemeen vertrouwen" : "Overall confidence"}: {overallConfidence}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onCopy && (
          <Button variant="outline" size="sm" onClick={onCopy} className="rounded-xl" data-testid="synberry-copy-button">
            {nl ? "Markdown kopieren" : "Copy markdown"}
          </Button>
        )}
        {onDownloadMd && (
          <Button variant="outline" size="sm" onClick={onDownloadMd} className="rounded-xl" data-testid="synberry-download-md-button">
            {nl ? "Download .md" : "Download .md"}
          </Button>
        )}
        {onDownloadPdf && (
          <Button size="sm" onClick={onDownloadPdf} className="rounded-xl bg-[#61CBA3] text-white hover:bg-[#4DAF8B]" data-testid="synberry-download-pdf-button">
            {nl ? "Download PDF" : "Download PDF"}
          </Button>
        )}
      </div>
    </div>
  );
}
